import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { LogIn, Mail, Lock, ArrowLeft } from "lucide-react";
import { LOGOS } from "../../lib/brand";
import { useAuth } from "../../context/AuthContext";
import { formatApiError } from "../../lib/api";
import { RotatingLogo } from "../../components/Wavy";

const ConsoleLogin = () => {
  const nav = useNavigate();
  const { user, login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (user && user !== false) nav("/console");
  }, [user, nav]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setBusy(true);
    try {
      await login(email, password);
      nav("/console");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-cream grid md:grid-cols-2">
      <div className="relative hidden md:flex bg-magenta overflow-hidden items-center justify-center">
        <div className="absolute top-8 left-8 z-10">
          <Link to="/" className="inline-flex items-center gap-2 text-cream/90 font-serif-display uppercase text-xs tracking-[0.22em] hover:text-cream">
            <ArrowLeft className="w-4 h-4" /> BACK TO SITE
          </Link>
        </div>
        <div className="w-full max-w-[420px] px-10">
          <RotatingLogo src={LOGOS.bloomFoundry} alt="The Bloom Foundry" />
          <h1 className="font-serif-display uppercase text-4xl text-cream leading-[0.95] text-center mt-10">
            THE FOUNDRY CONSOLE
          </h1>
          <p className="mt-4 text-cream/85 text-center">
            The beautifully organised studio hub where Heritage and The Etchery make the magic happen.
          </p>
        </div>
      </div>

      <div className="flex items-center justify-center p-6 md:p-14">
        <div className="w-full max-w-md">
          <span className="font-serif-display uppercase tracking-[0.32em] text-magenta text-xs font-semibold">FOUNDRY CONSOLE</span>
          <h2 className="font-serif-display uppercase text-4xl sm:text-5xl text-plum-deep mt-3">WELCOME BACK, MAKER</h2>
          <p className="mt-3 text-plum-deep/70">Sign in to manage the whole Bloom Foundry.</p>

          <form onSubmit={submit} className="mt-10 space-y-5" data-testid="console-login-form">
            <label className="block">
              <span className="block text-xs uppercase tracking-[0.22em] text-plum-deep/60 mb-2 font-semibold">EMAIL</span>
              <div className="relative">
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-plum-deep/40" />
                <input
                  type="email"
                  required
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  data-testid="console-login-email"
                  className="w-full rounded-full pl-12 pr-5 py-3.5 bg-cream border border-plum-deep/15 focus:outline-none focus:border-magenta"
                />
              </div>
            </label>
            <label className="block">
              <span className="block text-xs uppercase tracking-[0.22em] text-plum-deep/60 mb-2 font-semibold">PASSWORD</span>
              <div className="relative">
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-plum-deep/40" />
                <input
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="console-login-password"
                  className="w-full rounded-full pl-12 pr-5 py-3.5 bg-cream border border-plum-deep/15 focus:outline-none focus:border-magenta"
                />
              </div>
            </label>
            {error && (
              <p className="text-sm text-red-600 bg-red-50 rounded-2xl px-4 py-3" data-testid="console-login-error">{error}</p>
            )}
            <button
              type="submit"
              disabled={busy}
              data-testid="console-login-submit"
              className="w-full inline-flex items-center justify-center gap-3 bg-magenta text-cream rounded-full py-4 font-serif-display uppercase text-sm tracking-[0.2em] font-semibold hover:bg-plum transition-colors disabled:opacity-60"
            >
              <LogIn className="w-4 h-4" /> {busy ? "SIGNING IN…" : "ENTER CONSOLE"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default ConsoleLogin;
